"use client";

import { QuestType } from "@/components/Activation/ActivationPanel/ActivationPanel.utils";
import { detectUserAgent } from "@/components/Discover/utils";
import type { Game } from "@/components/Onboarding/constants";
import { useQuestCompletionsV2 } from "@/components/QuestsV2/useQuestCompletions";
import { AppleLogo } from "@/components/icons/AppleLogo";
import { CheckIcon } from "@/components/icons/CheckIcon";
import { GooglePlayLogo } from "@/components/icons/GooglePlayLogo";
import { WindowsLogo } from "@/components/icons/WindowsLogo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Action,
  ControlType,
  useOnboardingAnalytics,
} from "@/context/AnalyticsProvider";
import { cn } from "@/lib/utils";
import { ExternalLinkIcon, Hourglass, X } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { LensFlareBackground } from "./LensFlareBackground";
import type { Quest } from "./OnboardingActivation";

interface DownloadLoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  game: Game;
  quest: Quest;
}

export const DownloadLoginModal = ({
  open,
  onOpenChange,
  game,
  quest,
}: DownloadLoginModalProps) => {
  const { track } = useOnboardingAnalytics();
  const requiresDownload = quest.type === QuestType.DOWNLOAD_LOGIN_PLAY;
  const hasTrackedView = useRef(false);

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    setStepsCompleted(0);
  };

  const { completedQuests } = useQuestCompletionsV2();
  const questCompleted = Boolean(
    completedQuests?.[`${game.id}-${quest.questId}`]?.completion_count,
  );

  const [stepsCompleted, setStepsCompleted] = useState(0);

  useEffect(() => {
    if (questCompleted) {
      track({
        action: Action.SUCCEEDED,
        extras: {
          id: `${game.id}-${quest.questId}`,
          gameId: game.id,
          gameName: game.name,
        },
      });
      setStepsCompleted(2);
    }
  }, [questCompleted, track, game.id, game.name, quest.questId]);

  useEffect(() => {
    if (open && !hasTrackedView.current) {
      hasTrackedView.current = true;
      track({
        action: Action.VIEWED,
        extras: {
          id: "download-login-modal",
          gameId: game.id,
          gameName: game.name,
        },
      });
    }

    if (!open) {
      hasTrackedView.current = false;
    }
  }, [open, track, game.id, game.name]);

  const handleGameTrigger = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: requiresDownload ? "download-login-modal" : "login-play-modal",
        gameId: game.id,
        gameName: game.name,
        control: requiresDownload ? "download-login" : "login-play",
        controlType: ControlType.BUTTON,
      },
    });

    if (!requiresDownload) {
      window.open(quest.externalUrl, "_blank", "noopener,noreferrer");
      setStepsCompleted(1);
      return;
    }

    const userAgent = navigator.userAgent;
    const downloadUrl = game[detectUserAgent(userAgent)];
    window.open(downloadUrl, "_blank", "noopener,noreferrer");
    setStepsCompleted(1);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        overlayClassName="bg-neutral-900/50"
        className="bg-transparent backdrop-blur-xl h-full w-[clamp(18.75rem,92vw,24.25rem)] max-h-[min(42rem,calc(100vh-2rem))] overflow-hidden !rounded-[1.25rem] p-0 border-2 border-white/20 [&>button:last-child]:hidden"
      >
        <DialogTitle className="sr-only">{`Download & Login ${game.name}`}</DialogTitle>
        <LensFlareBackground />

        <div className="relative flex flex-col justify-evenly p-6">
          {game.banner ? (
            <div className="relative mx-auto">
              <Image
                src="/images/onboarding/game-banner-bg.webp"
                alt=""
                width={400}
                height={400}
                className="object-cover rounded-full size-64 brightness-[0.25]"
              />
              <Image
                src={game.banner}
                alt={`${game.name} logo`}
                width={400}
                height={400}
                className="size-64 p-4 absolute top-0.5"
              />
            </div>
          ) : null}

          <div className="text-center space-y-2">
            <h1 className="font-bold text-white text-[clamp(1.125rem,3dvh,1.875rem)] leading-tight">
              {game.name}
            </h1>

            <div className="flex gap-x-1 items-center justify-center text-xs">
              <div className="px-2 py-1.5 bg-neutral-900 rounded-md flex gap-x-1 border border-white/10">
                {game.Android ? <GooglePlayLogo className="size-3" /> : null}
                {game.iOS ? <AppleLogo className="size-3" /> : null}
                {game.Windows ? <WindowsLogo className="size-3" /> : null}
                {game.General ? <ExternalLinkIcon className="size-3" /> : null}
              </div>

              <div className="px-2 py-1 bg-neutral-900 rounded-md flex gap-x-1 border items-center border-white/10">
                <Hourglass className="size-3" />
                <p>2 MINS</p>
              </div>

              <div className="px-2 py-1 bg-neutral-900 rounded-md flex gap-x-1 border border-white/10">
                {game.genre?.join(", ")}
              </div>
            </div>

            {game.description ? (
              <p className="text-xs text-text-body-secondary font-roboto">
                {game.description}
              </p>
            ) : null}
          </div>

          {/* Steps */}
          <div className="p-6 rounded-lg bg-neutral-100/10 relative gap-y-3 flex flex-col text-xs font-medium">
            <div className="flex gap-x-4 relative">
              <div className="size-9 rounded-full border border-white/10 flex items-center justify-center text-base font-medium">
                {stepsCompleted >= 1 ? <CheckIcon className="size-4" /> : 1}
              </div>
              <div className="space-y-1.5">
                <p>
                  {requiresDownload ? "Download" : "Launch"} {game.name}
                </p>
                <Button onClick={handleGameTrigger}>
                  {requiresDownload ? "Download" : "Launch"} game
                  <ExternalLinkIcon className="size-4 mb-0.5" />
                </Button>
              </div>

              <div className="w-px top-9 h-[calc(100%-1.5rem)] bg-neutral-300/30 absolute left-[1.1rem]" />
            </div>

            <div className="flex gap-x-4 relative">
              <div className="size-9 rounded-full border border-white/10 flex items-center justify-center text-base font-medium">
                {stepsCompleted > 1 ? <CheckIcon className="size-4" /> : 2}
                <div
                  className={cn(
                    "absolute border-transparent rounded-full border-t-status-success size-7 animate-rotate-border",
                    stepsCompleted === 1 ? "border-2" : "border-0",
                  )}
                />
              </div>
              <div className="space-y-1 flex flex-col justify-center">
                <p>Sign in with Immutable</p>
                {stepsCompleted === 1 ? (
                  <p className="text-muted-foreground">
                    ...waiting for you to sign in
                  </p>
                ) : null}
              </div>

              <div className="w-px top-9 h-[calc(100%-1.5rem)] bg-neutral-300/30 absolute left-[1.1rem]" />
            </div>

            <div className="flex gap-x-4">
              <div className="size-9 rounded-full border border-white/10 flex items-center justify-center text-base font-medium">
                {stepsCompleted === 2 ? <CheckIcon className="size-4" /> : 3}
              </div>
              <div className="space-y-1 flex flex-col justify-center">
                <p>Come back to play.immutable.com</p>
                {stepsCompleted === 2 ? (
                  <p className="text-status-success">Gems claimed!</p>
                ) : (
                  <p className="text-muted-foreground">Claim your gems!</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogClose
          className="absolute right-4 top-4 size-6 rounded-full bg-[#78716C] text-[#E0E0E0] transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 p-1"
          onClick={() => handleOpenChange(false)}
        >
          <X
            className="size-[0.875rem] mx-auto stroke-neutral-800"
            strokeWidth={4}
          />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};
